/***
 * Created on 2023/10/12
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import sharedI18n from "common/SharedI18n";
import {useTranslation, I18nextProvider} from "react-i18next";
import OtherWaysComponent from "./other_ways_component";
import {QueryClientProvider, QueryClient, useQuery, useMutation} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../service/api";
import {useLanguage, useProfile} from "common/Hook";
import {getPrimaryEmail} from "common/ServiceUser";
import {getErrorMessage, getCurrentDomainRegion} from "common/ServiceCommon";
import {useBoundedStore} from 'common/Store';
import ObserverChangePasswordDialog from "./observer_change_password_dialog";
import SetupAuthenticatorDialog from "./setup_authenticator_dialog";
import Cookies from "js-cookie";
import NotReceiveCodeDialog from "./not_recieve_code_dialog";
import SmsEmailVerificationComponent from "./sms_email_verification_component";
import QnapAuthVerificationComponent from "./qnap_auth_verification_component";


const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // default: true
        },
    },
});

function TwoStepVerificationComponent(props) {
    const { t, i18n } = useTranslation();
    useLanguage(i18n);
    const [showOtherWays, setShowOtherWays] = useState(false);
    // const [errorMessage, setErrorMessage] = useState();
    const [method, setMethod] = useState(sessionStorage.getItem('twostep_method'));
    const [showObserverDialog, setShowObserverDialog] = useState(false);
    const [showAuthWarning, setShowAuthWarning] = useState(false);
    const [showNotReceiveCode, setShowNotReceiveCode] = useState(false);
    const {profile} = useProfile();
    const email = getPrimaryEmail(profile);
    const is_observer = document.cookie.split(';').find(e => e.trim().startsWith('observer'))?.trim()?.split('=')
    const store = useBoundedStore();

    const twoStepInfo = useQuery({
        queryKey: ['two_step_info'],
        queryFn: async () => await apiGet('/ajax/2fa')
    });

    const sendTwoStepMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/send_two_step_code', data),
        onSuccess: data => {
            if (data instanceof Response && !data.ok) {
                store.globalAlert.setAlert(getErrorMessage(t, 1019010), 'error', true);
            }
            queryClient.invalidateQueries({queryKey: ['get_countdown']});
            queryClient.invalidateQueries({queryKey: ['two_step_info']});
        },
        onError: (error, variables, context) => {
            store.globalAlert.setAlert(getErrorMessage(t, error.cause?.code), 'error', true);
        }
    });

    const verifyTwoStepMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/2fa', data),
        onSuccess: data => {
            if (!is_observer) {
                const noshow = Cookies.get('noAuthenticatorWarning');
                const is_mobile_app = Cookies.get('mobile_app');
                const region = getCurrentDomainRegion();
                if (!profile?.secure?.google_auth && noshow !== 'true' && is_mobile_app !== 'true' && region !== 'CHINA') {
                    setShowAuthWarning(true);
                }
                else {
                    const url = new URL(location.href);
                    const state = url.searchParams.get('state');
                    const lang = url.searchParams.get('lang');
                    if (state && state !== '') {
                        // location.href = `${state}&lang=${lang}`;
                        location.href = `/login?${url.searchParams.toString()}`
                    }
                    else {
                        location.href = '/login';
                    }
                }
            }
            else {
                setShowObserverDialog(true);
            }
        },
        onError: (error, variables, context) => {
            console.log(error);
            queryClient.invalidateQueries({
                queryKey: ['get_captcha_image']
            });
            if (error?.cause?.code !== 1019016) {
                store.globalAlert.setAlert(getErrorMessage(t, error.cause?.code), 'error', true);
            }
        }
    })

    const sendTwoStepCode = async (send_type) => {
        store.globalAlert.setAlert('', 'error', false);
        const data = {
            send_type
        }
        await sendTwoStepMutation.mutateAsync(data);
    }

    const signout = () => {
        return window.location.replace(`/signout${location.search}`);
    }

    const setTwoStepType = (type) => {
        sessionStorage.setItem('twostep_method', type);
        setMethod(type);
    }

    const onLaunchQnapAuth = (event, qrcode=null) => {
        let uri = `authqnap://qidtwostep?userid=${profile?.user_id}&verifytype=${method === 'google_auth' ? 'totp': method}`
        if (qrcode) {
            uri = `${uri}&qrcodeurl=${qrcode}`
        }
        window.open(uri, '_blank');
    }

    useEffect(() => {
        if (twoStepInfo?.data && twoStepInfo.isSuccess) {
            const twostep_method = sessionStorage.getItem('twostep_method');
            let type = !['undefined', 'null'].includes(twostep_method || 'null') ? twostep_method : twoStepInfo?.data?.qnap_auth?.default_method;
            if (['google_auth', 'totp'].includes(type)) {
                type = 'google_auth';
            }
            setMethod(type);
            sessionStorage.setItem('twostep_method', type);
        }
    }, [twoStepInfo.data, twoStepInfo.isSuccess]);

    return (
        <section className="common-page-wrapper common-page-wrapper--2-step-verification">
            <div className="common-page-header">{t('@2-Step Verification@')}</div>
            { !showOtherWays &&
                <>
                    { ['email', 'sms', 'sms_1', 'sms_2', 'google_auth'].includes(method) &&
                        <SmsEmailVerificationComponent method={method} twoStepInfo={twoStepInfo}
                                                       isObserver={is_observer} onLaunchQnapAuth={onLaunchQnapAuth}
                                                       signout={signout} verifyTwoStepMutate={verifyTwoStepMutation}
                                                       otherWay={e => {e.preventDefault();setShowOtherWays(true);}}
                                                       noCode={e => {e.preventDefault();setShowNotReceiveCode(true);}}
                                                       sendTwoStepCode={sendTwoStepCode}/>
                    }
                    {!['email', 'sms', 'sms_1', 'sms_2', 'google_auth'].includes(method) &&
                        <QnapAuthVerificationComponent method={method}
                                                       twoStepInfo={twoStepInfo}
                                                       isObserver={is_observer}
                                                       onLaunchQnapAuth={onLaunchQnapAuth} signout={signout}
                                                       otherWay={e => {e.preventDefault();setShowOtherWays(true);}}/>
                    }
                </>
            }

            {showOtherWays && <OtherWaysComponent
                goBack={()=>setShowOtherWays(false)}
                defaultMethod={method}
                setMethod={setTwoStepType}
                user={profile}
                email={email} sendTwoStepCode={sendTwoStepCode}/>}

            {showObserverDialog && <ObserverChangePasswordDialog showModal={showObserverDialog} />}
            {showAuthWarning && <SetupAuthenticatorDialog showModal={showAuthWarning} />}
            {showNotReceiveCode && <NotReceiveCodeDialog showModal={showNotReceiveCode} closeModal={()=>setShowNotReceiveCode(false)}/>}
        </section>
    );
}

export function TwoStepVerificationIndex() {
    return (
        <I18nextProvider i18n={sharedI18n} >
            <QueryClientProvider client={queryClient}>
                <TwoStepVerificationComponent />
            </QueryClientProvider>
        </I18nextProvider>
    );
}

export default TwoStepVerificationIndex;